import { isLoggedInAtom, isOnboardingAtom, searchObjectQuery, userInfoAtom } from 'app/atoms';
import { useNeedsAppUpdate } from 'app/atoms/needs-app-update';
import { useTopLoadingIndicator } from 'app/components/top-loading-indicator';

import { useScopedTranslation } from 'app/hooks/i18n';
import { useDeviceWidth } from 'app/hooks/use-device-size';
import { StorageKey, useStoredValue } from 'app/lib/storage';
import { useAllCategories } from 'app/network/category';
import { getListingsFilters, ListingsSortBy, useAllListings, useAllTags } from 'app/network/listing';
import { useOnePreference, useUpsertOnePreference } from 'app/network/preferences';
import { useTrialFreeSubAvailable } from 'app/network/promotions';
import { useFindUsers } from 'app/network/user';
import { LPCEvent, useIsEventActive } from 'app/services/events';
import { ListingSubtype, ListingType } from 'app/types/listing';
import type { SpecificNavigationProp, SpecificRouteProp } from 'app/types/navigation';
import { useIsGuestWeb, usePageTitle, useUISize } from 'app/utils/web-helpers';
import { useAtom, useAtomValue } from 'jotai';
import { createContext, type SetStateAction, useContext, useEffect, useRef, useState } from 'react';
import type { FilterPreferences } from 'app/types/listings-filters';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { useSharedValue, withSpring } from 'react-native-reanimated';
import Toast from 'react-native-toast-message';
import { createTheme as createDSTheme } from 'app/shopify';
import { useFilterPreferences } from 'app/screens/billboard/all-listings/components/listings-filters';
const theme = createDSTheme();

export const AllListingsFilterContext = createContext<FilterPreferences | null>(null);

export interface UseAllListingsScreenParams {
  navigation: SpecificNavigationProp<'all-listings'>;
  route: SpecificRouteProp<'all-listings'>;
}

function useListingsSearch({ navigation, route }: UseAllListingsScreenParams) {
  const [atomSearch, setAtomSearch] = useAtom(searchObjectQuery);

  const setSearch = (value: string) => {
    const nextSearch = value.trim();
    setAtomSearch(nextSearch);
    navigation.setParams({ search: nextSearch || undefined });
  };

  // Sync search state when route params change (e.g., from Header search submission).
  // Only apply when `search` is present on params — tab changes and other setParams calls
  // must not reset an active query back to '' and reload the unfiltered feed.
  useEffect(() => {
    if (route.params === undefined || !('search' in route.params)) {
      return;
    }
    const paramSearch = route.params.search ?? '';
    if (paramSearch !== atomSearch) {
      setAtomSearch(paramSearch);
    }
  }, [atomSearch, route.params, setAtomSearch]);

  const search = route.params && 'search' in route.params ? (route.params.search ?? '') : atomSearch;
  return { search, setSearch };
}

function useLevelUpModal(userNowContributor: boolean) {
  const [levelUpModalState, setLevelUpModalState] = useState(() => ({
    trigger: userNowContributor,
    open: userNowContributor,
  }));
  const isLevelUpModalOpen =
    levelUpModalState.trigger === userNowContributor
      ? levelUpModalState.open
      : userNowContributor || levelUpModalState.open;

  if (levelUpModalState.trigger !== userNowContributor) {
    setLevelUpModalState({ trigger: userNowContributor, open: isLevelUpModalOpen });
  }

  const setIsLevelUpModalOpen = (value: SetStateAction<boolean>) => {
    setLevelUpModalState((current) => ({
      ...current,
      open: typeof value === 'function' ? value(current.open) : value,
    }));
  };

  return { isLevelUpModalOpen, setIsLevelUpModalOpen };
}

function isPromoBannerAvailable(
  eventIsActive: boolean,
  freeSubAvailable: boolean,
  freeSubNumDaysLeft: number | undefined,
) {
  return eventIsActive || (freeSubAvailable && typeof freeSubNumDaysLeft === 'number' && freeSubNumDaysLeft >= 0);
}

function usePeopleSearch(appliedFilters: FilterPreferences, isLoggedIn: boolean, search: string) {
  const normalizedSearch = search.trim();
  const groupIds =
    appliedFilters.selectedGroups.length > 0
      ? appliedFilters.selectedGroups
      : (appliedFilters.availableValues.groups?.map(({ id }) => id) ?? []);
  const addressIds =
    appliedFilters.selectedAddresses.length > 0
      ? appliedFilters.selectedAddresses
      : (appliedFilters.availableValues.addresses?.map(({ id }) => id) ?? []);
  const canSearch = isLoggedIn && normalizedSearch.length >= 1 && (groupIds.length > 0 || addressIds.length > 0);
  const query = useFindUsers(
    {
      addressIds,
      groupIds,
      search: normalizedSearch,
      sortBy: 'relevance',
    },
    { enabled: canSearch },
  );

  return { canSearch, query, showGroups: appliedFilters.selectedGroups.length === 0 };
}

function getUnseenCount(fetchedCount: number | undefined, dataUpdatedAt: number, clearedAt: number | undefined) {
  if (fetchedCount === undefined) return undefined;
  return clearedAt !== undefined && dataUpdatedAt <= clearedAt ? 0 : fetchedCount;
}

function useUnseenListingCounts(
  offersQuery: ReturnType<typeof useAllListings>,
  requestsQuery: ReturnType<typeof useAllListings>,
) {
  const [clearedAt, setClearedAt] = useState<Partial<Record<ListingType, number>>>({});
  const unseenOffers = getUnseenCount(
    offersQuery.data?.pages[0]?.totalUnseen,
    offersQuery.dataUpdatedAt,
    clearedAt[ListingType.offer],
  );
  const unseenRequests = getUnseenCount(
    requestsQuery.data?.pages[0]?.totalUnseen,
    requestsQuery.dataUpdatedAt,
    clearedAt[ListingType.request],
  );
  const lastExplorationDateMutation = useUpsertOnePreference('lastExplorationDate', {
    onSuccess: (_data, variables, _context) => {
      if (!variables) return;
      if (
        variables[ListingType.offer] &&
        Date.parse(variables[ListingType.offer]) > Date.now() - 10 * 1000 &&
        (unseenOffers ?? 0) > 0
      ) {
        setClearedAt((current) => ({ ...current, [ListingType.offer]: Date.now() }));
      }
      if (
        variables[ListingType.request] &&
        Date.parse(variables[ListingType.request]) > Date.now() - 10 * 1000 &&
        (unseenRequests ?? 0) > 0
      ) {
        setClearedAt((current) => ({ ...current, [ListingType.request]: Date.now() }));
      }
    },
  });

  return { lastExplorationDateMutation, unseenOffers, unseenRequests };
}

export function useAllListingsScreen({ navigation, route }: UseAllListingsScreenParams) {
  const { t } = useScopedTranslation();
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isGuestWeb = useIsGuestWeb();

  const { search, setSearch } = useListingsSearch({ navigation, route });

  const isDragonEventActive = useIsEventActive(LPCEvent.Dragon2024);

  const userNowContributor = Boolean(route.params?.userNowContributor);
  const { isLevelUpModalOpen, setIsLevelUpModalOpen } = useLevelUpModal(userNowContributor);

  const w = useDeviceWidth();

  const setPageTitle = usePageTitle('billboard');
  useEffect(() => setPageTitle('tabBarLabel'), [setPageTitle]);

  const userInfo = useAtomValue(userInfoAtom);
  const isOnboarding = useAtomValue(isOnboardingAtom);

  const contextFilters = useContext(AllListingsFilterContext);
  const fallbackFilters = useFilterPreferences(route.params?.categoryId, route.params?.tagId);
  const appliedFilters = contextFilters ?? fallbackFilters;
  const type = route.params?.type ?? ListingType.offer;
  const customTabs = userInfo?.featureFlags?.customTabs ?? [
    { name: ListingType.offer, filters: { type: ListingType.offer } },
    { name: ListingType.request, filters: { type: ListingType.request } },
  ];
  const offerForcedFilters = customTabs.find((tab) => tab.filters.type === ListingType.offer)?.filters;
  const requestForcedFilters = customTabs.find((tab) => tab.filters.type === ListingType.request)?.filters;

  const guestLocationFilters =
    isGuestWeb && route.params?.searchCenterLatitude !== undefined && route.params.searchCenterLongitude !== undefined
      ? {
          searchCenterLatitude: route.params.searchCenterLatitude,
          searchCenterLongitude: route.params.searchCenterLongitude,
          searchDistance: route.params.searchDistance,
        }
      : {};

  const {
    canSearch: canSearchPeople,
    query: peopleQuery,
    showGroups: showPeopleGroups,
  } = usePeopleSearch(appliedFilters, isLoggedIn, search);

  const filtersOffers = {
    ...getListingsFilters(
      appliedFilters.forQuery,
      search,
      ListingType.offer,
      isLoggedIn,
      offerForcedFilters,
      route.params?.sort,
    ),
    ...guestLocationFilters,
  };

  const filtersRequests = {
    ...getListingsFilters(
      appliedFilters.forQuery,
      search,
      ListingType.request,
      isLoggedIn,
      requestForcedFilters,
      route.params?.sort,
    ),
    ...guestLocationFilters,
  };

  const offersQuery = useAllListings(filtersOffers, 'ListingList');
  const requestsQuery = useAllListings(filtersRequests, 'ListingList');

  const { data: categories } = useAllCategories();
  const activeCategoryId = appliedFilters.categoriesState[0]?.[0];
  const activeCategory = categories?.find((cat) => cat.id === activeCategoryId);

  const { data: allTags } = useAllTags();
  const activeTagId = appliedFilters.tagsState[0]?.[0];
  const activeTag = allTags?.find((tag) => tag.id === activeTagId);

  const hideOffersWithFee = appliedFilters.hideOffersWithFee;
  const hideFoundRequests = appliedFilters.hideFoundRequests;
  const dismissedOnly = appliedFilters.dismissedOnly;

  const currentFilters = type === ListingType.offer ? filtersOffers : filtersRequests;

  const forcedFilters = customTabs.find((tab) => tab.name === type)?.filters ?? { type };
  const listsServicesOnly =
    forcedFilters.subtypes?.length === 1 && forcedFilters.subtypes[0] === ListingSubtype.service;

  const sortOptions = (() => {
    const baseOptions = [...(currentFilters.search.trim().length > 0 ? [ListingsSortBy.Relevance] : [])];

    if (listsServicesOnly) {
      return [...baseOptions, ListingsSortBy.StartDate, ListingsSortBy.Distance, ListingsSortBy.Date];
    }

    if (currentFilters.type === ListingType.offer) {
      return [
        ...baseOptions,
        ListingsSortBy.Distance,
        ListingsSortBy.Date,
        ListingsSortBy.Popularity,
        ListingsSortBy.Random,
      ];
    }

    return [...baseOptions, ListingsSortBy.Distance, ListingsSortBy.Date, ListingsSortBy.Urgency];
  })();

  // Detect when a new address has been added from a modal
  const { addresses } = appliedFilters.availableValues;
  const previousAddressCount = useRef<number | null>(null);

  const newAddressBadgeState = useState<boolean>(false);
  const [, setShowNewAddressBadge] = newAddressBadgeState;
  const setSelectedAddresses = appliedFilters.setSelectedAddresses;

  useEffect(() => {
    if (!addresses) return;
    const timeout = setTimeout(() => {
      const priorCount = previousAddressCount.current;
      previousAddressCount.current = addresses.length;
      if (priorCount === null || addresses.length <= priorCount) return;

      // A new address has been added. Select it automatically.
      const lastAddress = addresses[addresses.length - 1];
      if (lastAddress?.id) {
        setSelectedAddresses((previousAddresses) => [...previousAddresses, lastAddress.id]);
      }

      if (isOnboarding) return;
      Toast.show({
        type: 'success',
        position: 'bottom',
        bottomOffset: 108,
        text1: t('addressAddedToast.title', { ns: 'all-listings' }),
        text2: t('addressAddedToast.text', { ns: 'all-listings' }),
        visibilityTime: 15000,
      });
      setShowNewAddressBadge(true);
    }, 0);
    return () => clearTimeout(timeout);
  }, [addresses, isOnboarding, setSelectedAddresses, setShowNewAddressBadge, t]);

  const { needsAppUpdate, openAppStore, appStoreUrl } = useNeedsAppUpdate();

  const { freeSubAvailable, freeSubNumDaysLeft } = useTrialFreeSubAvailable()();
  const shouldShowPromoBanner = isPromoBannerAvailable(
    isDragonEventActive(),
    Boolean(freeSubAvailable),
    typeof freeSubNumDaysLeft === 'number' ? freeSubNumDaysLeft : undefined,
  );

  const { lastExplorationDateMutation, unseenOffers, unseenRequests } = useUnseenListingCounts(
    offersQuery,
    requestsQuery,
  );

  const unseenPerTab = {
    [ListingType.offer]: unseenOffers,
    [ListingType.request]: unseenRequests,
  };

  // route.params.categoryId can change if the user clicks a category in the listing screen, which returns them to this screen with route.params.categoryId set
  const setFilterCategories = appliedFilters.categoriesState[1];
  useEffect(() => {
    if (route.params?.categoryId) {
      setFilterCategories([route.params.categoryId]);
    } else {
      setFilterCategories(undefined);
    }
  }, [route.params?.categoryId, setFilterCategories]);
  const setFilterTags = appliedFilters.tagsState[1];
  useEffect(() => {
    if (route.params?.tagId) {
      setFilterTags([route.params.tagId]);
    } else {
      setFilterTags(undefined);
    }
  }, [route.params?.tagId, setFilterTags]);

  const [dragonButtonPosition, setDragonButtonPosition] = useState<number>(0);
  const dragonBannerOffsetAnimated = useSharedValue(0);
  const [dragonBannerFullyVisible, setDragonBannerFullyVisible] = useState(true);

  const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>, numListings: number) => {
    if (!shouldShowPromoBanner || numListings < 4) {
      // Not enough listings to ensure we can hide the top banner
      return;
    }
    if (dragonBannerFullyVisible && event.nativeEvent.contentOffset.y > 20) {
      setDragonBannerFullyVisible(false);
      dragonBannerOffsetAnimated.set(
        withSpring(-dragonButtonPosition + theme.spacing['spacing.general.x4'], {
          overshootClamping: true,
        }),
      );
    } else if (!dragonBannerFullyVisible && event.nativeEvent.contentOffset.y <= 20) {
      setDragonBannerFullyVisible(true);
      dragonBannerOffsetAnimated.set(withSpring(0, { overshootClamping: true }));
    }
  };

  const loadingStateTab1 = useTopLoadingIndicator();
  const loadingStateTab2 = useTopLoadingIndicator();
  const { isLargeUI } = useUISize();
  // on mobile, we allow the user to choose between list or grid display
  // on web, the numColumns calculation is handled inside listing-list
  const [listFormat, setListFormat] = useStoredValue<'list' | 'grid'>(StorageKey.ListFormat, 'grid');
  const [explorationMode, setExplorationMode] = useStoredValue<'list' | 'map'>(StorageKey.ExplorationMode, 'list');

  const lastExplorationDateQuery = useOnePreference('lastExplorationDate');
  const onTabChanged = async (tab: ListingType) => {
    navigation.setParams({ type: tab });
    if (isLoggedIn && tab === ListingType.request) {
      // Mark all requests as seen
      const returned = await lastExplorationDateQuery.refetch();
      lastExplorationDateMutation.mutate({
        ...(returned.data?.value ?? {}),
        [ListingType.request]: new Date().toISOString(),
      });
    }
  };

  return {
    t,
    isLoggedIn,
    search,
    setSearch,
    isDragonEventActive,
    isLevelUpModalOpen,
    setIsLevelUpModalOpen,
    w,
    userInfo,
    appliedFilters,
    newAddressBadgeState,
    needsAppUpdate,
    openAppStore,
    appStoreUrl,
    freeSubAvailable,
    freeSubNumDaysLeft,
    shouldShowPromoBanner,
    unseenOffers,
    unseenRequests,
    unseenPerTab,
    lastExplorationDateMutation,
    dragonButtonPosition,
    setDragonButtonPosition,
    dragonBannerOffsetAnimated,
    onScroll,
    loadingStateTab1,
    loadingStateTab2,
    isLargeUI,
    listFormat,
    setListFormat,
    explorationMode: explorationMode ?? 'list',
    setExplorationMode,
    onTabChanged,
    navigation,
    route,
    offersTotal: offersQuery.data?.pages[0]?.total ?? 0,
    requestsTotal: requestsQuery.data?.pages[0]?.total ?? 0,
    hideOffersWithFee,
    hideFoundRequests,
    dismissedOnly,
    activeCategoryId,
    activeCategory,
    activeTagId,
    activeTag,
    currentFilters,
    forcedFilters,
    guestLocationFilters,
    sortOptions,
    canSearchPeople,
    peopleQuery,
    showPeopleGroups,
  };
}
